import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Group,
  Modal,
  NumberInput,
  Select,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { apiFetch, apiPost, type ApiError } from "../../api/client.js";
import { QuotaBar } from "./quota-bar.js";

interface CreateOptionsIso {
  id: string;
  display_name: string;
  size_bytes: number;
  present: boolean;
}

interface CreateOptions {
  defaults: { node: string };
  isos: CreateOptionsIso[];
  quota: { cpu_cores: number; ram_mb: number; disk_gb: number };
  usage: { cpu_cores: number; ram_mb: number; disk_gb: number } | null;
  usage_unavailable?: boolean;
  remaining: { cpu_cores: number; ram_mb: number; disk_gb: number } | null;
}

const NAME_RE = /^[a-zA-Z]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?$/;

interface CreateVmModalProps {
  opened: boolean;
  onClose: () => void;
  onCreated: () => void;
}

/*
 * Create VM wizard (Phase 5). Fetches /api/create-options on open, renders a
 * live quota-remaining indicator (progress bars) that updates as the customer
 * edits the inputs, and disables submit when requested > remaining or the name
 * is invalid. The create call is synchronous (up to ~60s with a spinner); on
 * 201 the modal closes + the VM list refetches. Error mapping:
 *   409 quota_exceeded -> red "Quota exceeded: <field>"
 *   502 pve_unavailable / create_failed -> red "Proxmox error during create."
 *   400 invalid_request -> inline field errors
 *   403 not_in_allowlist -> red "This ISO is not allowed."
 */
export function CreateVmModal({ opened, onClose, onCreated }: CreateVmModalProps) {
  const [options, setOptions] = useState<CreateOptions | null>(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [name, setName] = useState("");
  const [cores, setCores] = useState<number | "">("");
  const [memoryMb, setMemoryMb] = useState<number | "">("");
  const [diskGb, setDiskGb] = useState<number | "">("");
  const [isoId, setIsoId] = useState<string | null>(null);
  const [memUnit, setMemUnit] = useState<"mb" | "gb">("gb");
  const [issues, setIssues] = useState<Record<string, string>>({});

  async function loadOptions() {
    setLoading(true);
    try {
      const data = await apiFetch<CreateOptions>("/api/create-options");
      setOptions(data);
      // Seed defaults at the smaller end of common values.
      setCores(1);
      setDiskGb(10);
      setMemoryMb(1024);
      setIsoId("__none__");
    } catch (err) {
      notifications.show({ color: "red", message: friendly(err, "Could not load create options.") });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (opened) {
      setName("");
      setIssues({});
      setMemUnit("gb");
      void loadOptions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened]);

  const usageUnavailable = !!options?.usage_unavailable;
  const remaining = options?.remaining ?? { cpu_cores: 0, ram_mb: 0, disk_gb: 0 };
  const quota = options?.quota ?? { cpu_cores: 0, ram_mb: 0, disk_gb: 0 };

  const reqCores = typeof cores === "number" ? cores : 0;
  const reqRam = typeof memoryMb === "number" ? memoryMb : 0;
  const reqDisk = typeof diskGb === "number" ? diskGb : 0;

  const overCores = reqCores > quota.cpu_cores;
  const overRam = reqRam > remaining.ram_mb;
  const overDisk = reqDisk > remaining.disk_gb;
  const nameInvalid = name.length > 0 && !NAME_RE.test(name);

  const canSubmit =
    !submitting &&
    !usageUnavailable &&
    NAME_RE.test(name) &&
    typeof cores === "number" &&
    typeof memoryMb === "number" &&
    typeof diskGb === "number" &&
    !overCores &&
    !overRam &&
    !overDisk;

  const isoSelectData = useMemo(() => {
    const list = options?.isos ?? [];
    return [
      { value: "__none__", label: "None" },
      ...list.map((i) => ({
        value: i.id,
        label: i.present ? i.display_name : `${i.display_name} (unavailable)`,
      })),
    ];
  }, [options]);

  async function submit() {
    if (!canSubmit || typeof cores !== "number" || typeof memoryMb !== "number" || typeof diskGb !== "number") return;
    setSubmitting(true);
    setIssues({});
    const body: Record<string, unknown> = {
      name,
      cores,
      memory_mb: memoryMb,
      disk_gb: diskGb,
    };
    if (isoId && isoId !== "__none__") body.iso_id = isoId;
    try {
      const res = await apiPost<{ vmid: number; name: string } & Record<string, unknown>>("/api/vms", body);
      notifications.show({ color: "green", message: `VM ${res.name} created (vmid ${res.vmid}).` });
      onCreated();
      onClose();
    } catch (err) {
      const e = err as ApiError;
      const detail = e.details as Record<string, unknown> | undefined;
      if (e.status === 409 && e.error === "quota_exceeded") {
        const excess = detail?.excess as Record<string, number> | undefined;
        const fields: string[] = [];
        if (excess?.cpu_cores) fields.push(`CPU +${excess.cpu_cores}`);
        if (excess?.ram_mb) fields.push(`RAM +${excess.ram_mb} MB`);
        if (excess?.disk_gb) fields.push(`Disk +${excess.disk_gb} GB`);
        notifications.show({
          color: "red",
          message: `Quota exceeded: ${fields.join(", ") || "insufficient headroom"}.`,
        });
      } else if (e.status === 502 && (e.error === "pve_unavailable" || e.error === "create_failed")) {
        notifications.show({ color: "red", message: "Proxmox error during create. Try again or contact support." });
      } else if (e.status === 403 && e.error === "not_in_allowlist") {
        notifications.show({ color: "red", message: "This ISO is not allowed." });
      } else if (e.status === 400 && e.error === "invalid_request") {
        const fieldIssues: Record<string, string> = {};
        const raw = detail?.issues as Array<{ path: string[]; message: string }> | undefined;
        if (raw) {
          for (const i of raw) {
            const key = i.path[0] ?? "_";
            fieldIssues[key] = i.message;
          }
        }
        setIssues(fieldIssues);
      } else {
        notifications.show({ color: "red", message: friendly(err, "Create failed.") });
      }
    } finally {
      setSubmitting(false);
    }
  }

  // Memory input: display in the chosen unit but store MB in state.
  const memDisplay = memUnit === "gb" && typeof memoryMb === "number" ? memoryMb / 1024 : memoryMb;

  return (
    <Modal opened={opened} onClose={onClose} title="Create VM" size="lg" closeOnClickOutside={!submitting} closeOnEscape={!submitting}>
      <Stack gap="md">
        {loading && (
          <Text c="dimmed" size="sm">
            Loading options…
          </Text>
        )}

        {options && usageUnavailable && (
          <Alert color="yellow" variant="light" p="sm">
            Can't compute current usage (Proxmox unreachable). Please try again later.
          </Alert>
        )}

        {options && !usageUnavailable && (
          <>
            <Alert color="gray" variant="light" p="sm">
              <Stack gap="xs">
                <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                  Quota remaining
                </Text>
                <QuotaBar label="Memory" used={quota.ram_mb - remaining.ram_mb} total={quota.ram_mb} unit="MB" />
                <QuotaBar label="Disk" used={quota.disk_gb - remaining.disk_gb} total={quota.disk_gb} unit="GB" />
              </Stack>
            </Alert>

            <TextInput
              label="Name"
              placeholder="my-vm"
              value={name}
              onChange={(e) => setName(e.currentTarget.value)}
              error={nameInvalid ? "Must start with a letter. Letters, digits, and hyphens only (no underscores, no trailing hyphen). 1–63 chars." : issues.name}
              disabled={submitting}
            />

            <Group grow align="flex-start">
              <NumberInput
                label="CPU cores"
                value={cores}
                onChange={(v) => setCores(typeof v === "number" ? v : "")}
                min={1}
                max={Math.min(64, quota.cpu_cores)}
                error={overCores ? `Up to ${quota.cpu_cores} cores per VM.` : issues.cores}
                disabled={submitting}
              />
              <NumberInput
                label="Disk (GB)"
                value={diskGb}
                onChange={(v) => setDiskGb(typeof v === "number" ? v : "")}
                min={1}
                max={10000}
                error={overDisk ? `Only ${remaining.disk_gb} GB remaining.` : issues.disk_gb}
                disabled={submitting}
              />
            </Group>

            <Stack gap={4}>
              <Group justify="space-between" align="flex-end">
                <Box style={{ flex: 1 }}>
                  <NumberInput
                    label={memUnit === "gb" ? "Memory (GB)" : "Memory (MB)"}
                    value={memDisplay}
                    onChange={(v) => {
                      const n = typeof v === "number" ? v : 0;
                      setMemoryMb(memUnit === "gb" ? Math.round(n * 1024) : n);
                    }}
                    min={memUnit === "gb" ? 1 : 128}
                    max={
                      memUnit === "gb"
                        ? Math.min(256, Math.floor(remaining.ram_mb / 1024) || 1)
                        : Math.min(262144, Math.max(128, remaining.ram_mb))
                    }
                    error={overRam ? `Only ${remaining.ram_mb} MB remaining.` : issues.memory_mb}
                    disabled={submitting}
                  />
                </Box>
                <Button
                  variant="subtle"
                  size="xs"
                  onClick={() => setMemUnit((u) => (u === "gb" ? "mb" : "gb"))}
                  disabled={submitting}
                >
                  Switch to {memUnit === "gb" ? "MB" : "GB"}
                </Button>
              </Group>
            </Stack>

            <Select
              label="ISO (optional)"
              value={isoId}
              onChange={setIsoId}
              data={isoSelectData}
              disabled={submitting}
            />

            <Group justify="flex-end" mt="xs">
              <Button variant="default" onClick={onClose} disabled={submitting}>
                Cancel
              </Button>
              <Button onClick={() => void submit()} loading={submitting} disabled={!canSubmit}>
                Create VM
              </Button>
            </Group>
          </>
        )}
      </Stack>
    </Modal>
  );
}

function friendly(err: unknown, fallback: string): string {
  const e = err as ApiError | undefined;
  return e?.message || fallback;
}
